import React, { useState, useEffect, useContext, useCallback } from "react";
import { AuthContext } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { reportService, excavatorService, dashboardService, betriebstageBuchService } from "../services/api";
import moment from "moment";
import { Bar, Pie, Line, Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import { saveAs } from "file-saver";
import { toast } from "react-toastify"; // Add toast import

// MUI Components
import {
  Box, Button, Card, CardContent, CardHeader, CircularProgress, Grid, Paper, Select, MenuItem, TextField, Typography,
  Alert, AlertTitle, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Stack,
  FormControl, InputLabel, useTheme as useMuiTheme // MUI useTheme
} from "@mui/material";
import { alpha } from "@mui/material/styles";

// MUI Icons
import {
  BarChartOutlined as BarChartIcon, // Page Icon
  PrintOutlined as PrintIcon,
  CalendarTodayOutlined as CalendarIcon,
  FilterListOutlined as FilterIcon,
  RefreshOutlined as RefreshIcon,
  AssessmentOutlined as AssessmentIcon, // Overview
  PrecisionManufacturingOutlined as MachinesIcon, // Machines
  PeopleAltOutlined as PersonnelIcon, // Personnel
  InsightsOutlined as PredictionsIcon, // Predictions
  Inventory2Outlined as InventoryIcon, // Inventory
  MenuBookOutlined as BetriebstagebuchIcon, // Betriebstagebuch
  DownloadOutlined as DownloadCsvIcon,
  DescriptionOutlined as DescriptionIcon,
  CheckCircleOutline as CheckCircleIcon,
  WarningAmberOutlined as WarningIcon,
  ListAltOutlined as ListAltIcon,
  PendingOutlined as PendingIcon,
} from "@mui/icons-material";

import PageContainer from "../components/PageContainer";
// ContentSection might be too restrictive for this dashboard-like layout

ChartJS.register(...registerables); // Register all Chart.js components

// DetailItemCard component for stats display
const DetailItemCard = ({ icon, label, value, chipProps, accent, sx }) => {
  const muiTheme = useMuiTheme();
  return (
    <Paper variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2, bgcolor: accent ? alpha(muiTheme.palette.primary.main, 0.03) : 'transparent', borderColor: accent ? alpha(muiTheme.palette.primary.main, 0.3) : 'divider', ...sx }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5, color: accent ? 'primary.main' : 'text.secondary' }}>
        {icon && React.cloneElement(icon, { fontSize: 'small' })}
        <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 'medium' }}>{label}</Typography>
      </Stack>
      {chipProps ? <Chip size="small" {...chipProps} /> : <Typography variant="body1" fontWeight="medium" color="text.primary">{value || "-"}</Typography>}
    </Paper>
  );
};

// TabPanel helper
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`stats-tabpanel-${index}`} aria-labelledby={`stats-tab-${index}`} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const ReportStats = () => {
  const { t } = useLanguage();
  const theme = useMuiTheme(); // MUI theme

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  const [statsData, setStatsData] = useState({
    totalReports: 0, signedReports: 0, unsignedReports: 0, reportsByType: {}, monthlyReports: [],
    maintenanceByExcavator: [], topOperators: [], serviceTypes: {}, timeToApproval: 0,
    avgMaintenanceInterval: 0, criticalMachines: [], upcomingMaintenances: [],
  });
  const [inventoryStats, setInventoryStats] = useState({ total: 0, categories: [], lastUpdated: null, items: [] });
  const [betriebstagebuchStats, setBetriebstagebuchStats] = useState({ total: 0, approved: 0, unsigned: 0, thisMonth: 0, lastEntryDate: null, statusDistribution: {}, monthlyCounts: {}, topCreators: {} });

  const [dateRange, setDateRange] = useState({
    startDate: moment().subtract(6, "months").format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
  });
  const [selectedExcavator, setSelectedExcavator] = useState("all");
  const [selectedType, setSelectedType] = useState("all");
  const [excavatorList, setExcavatorList] = useState([]);

  const chartColors = [
    theme.palette.primary.main, theme.palette.secondary.main, theme.palette.success.main, theme.palette.error.main,
    theme.palette.warning.main, theme.palette.info.main, theme.palette.grey[500],
    alpha(theme.palette.primary.main, 0.7), alpha(theme.palette.secondary.main, 0.7), alpha(theme.palette.success.main, 0.7),
  ];
  const chartBorderColors = chartColors.map(color => alpha(color, 0.9));
  const chartBgColors = chartColors.map(color => alpha(color, 0.6));


  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const commonParams = { fromDate: dateRange.startDate, toDate: dateRange.endDate, limit: 10000 }; // Fetch all for client-side aggregation
      if (selectedExcavator !== "all") commonParams.excavatorId = selectedExcavator;
      if (selectedType !== "all") commonParams.type = selectedType;

      const [reportsRes, excavatorsRes, dashboardStatsRes, inventorySummaryRes, btbEntriesRes] = await Promise.all([
        reportService.getAll(commonParams),
        excavatorService.getAll({ limit: 10000 }), // For machine names and details
        dashboardService.getStats(), // For some summary stats
        dashboardService.getInventorySummary(),
        betriebstageBuchService.getAll({ ...commonParams, sortBy: 'date', sortDirection: 'desc' }) // Also filter BTB entries by date
      ]);

      const reports = reportsRes.data?.reports || reportsRes.data || [];
      const excavators = excavatorsRes.data?.data || excavatorsRes.data || [];
      if (excavatorList.length === 0 && excavators.length > 0) setExcavatorList(excavators);

      // Report Stats Processing (Simplified)
      const monthlyReports = Array(12).fill(0);
      reports.forEach(r => { monthlyReports[moment(r.maintenanceDate || r.createdAt).month()]++; });
      const reportsByType = reports.reduce((acc, r) => { acc[r.type || 'routine'] = (acc[r.type || 'routine'] || 0) + 1; return acc; }, {});

      setStatsData({
        totalReports: reports.length,
        signedReports: reports.filter(r => r.status === "signed" || r.status === "approved").length,
        unsignedReports: reports.filter(r => r.status !== "signed" && r.status !== "approved").length,
        reportsByType, monthlyReports,
        // More complex stats can be added back if needed, keeping it simpler for now
        maintenanceByExcavator: [], topOperators: [], serviceTypes: {}, timeToApproval: 0,
        avgMaintenanceInterval: 0, criticalMachines: [], upcomingMaintenances: [],
      });

      // Inventory Stats
      const inventoryItems = inventorySummaryRes.data || [];
      setInventoryStats({
        total: inventoryItems.length,
        categories: [...new Set(inventoryItems.map(item => item.type).filter(Boolean))],
        lastUpdated: inventoryItems.length > 0 ? inventoryItems.sort((a, b) => moment(b.updatedAt).diff(moment(a.updatedAt)))[0].updatedAt : null,
        items: inventoryItems,
      });

      // Betriebstagebuch Stats
      const btbEntries = btbEntriesRes.data?.data || btbEntriesRes.data || [];
      const btbStatusDist = btbEntries.reduce((acc, e) => { acc[e.status || 'unknown'] = (acc[e.status || 'unknown'] || 0) + 1; return acc; }, {});
      const btbMonthly = Array(6).fill(0); // Last 6 months
      const btbMonthsLabels = Array(6).fill("").map((_, i) => moment().subtract(5 - i, 'months').format('MMM YYYY'));
      btbEntries.forEach(e => { const monthIdx = btbMonthsLabels.indexOf(moment(e.date).format('MMM YYYY')); if (monthIdx > -1) btbMonthly[monthIdx]++; });
      const btbCreators = btbEntries.reduce((acc, e) => { acc[e.creator?.name || 'Unknown'] = (acc[e.creator?.name || 'Unknown'] || 0) + 1; return acc; }, {});

      setBetriebstagebuchStats({
        total: btbEntries.length,
        approved: btbEntries.filter(e => e.status === 'approved').length,
        unsigned: btbEntries.filter(e => e.status !== 'approved').length,
        thisMonth: btbEntries.filter(e => moment(e.date).isSame(moment(), 'month')).length,
        lastEntryDate: btbEntries.length > 0 ? moment(btbEntries[0].date).format("L") : null, // Assuming sorted by date desc
        statusDistribution: btbStatusDist,
        monthlyCounts: { labels: btbMonthsLabels, data: btbMonthly },
        topCreators: Object.entries(btbCreators).sort((a, b) => b[1] - a[1]).slice(0, 5).reduce((obj, [key, val]) => { obj[key] = val; return obj; }, {}),
        entries: btbEntries, // Store for CSV export
      });

    } catch (err) {
      console.error("Error fetching stats:", err);
      setError(t("errorFetchingStats") + (err.message ? `: ${err.message}` : ''));
    } finally { setLoading(false); }
  }, [dateRange, selectedExcavator, selectedType, t, excavatorList.length]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDateChange = (e) => setDateRange(prev => ({ ...prev, [e.target.name]: e.target.value }));
  const handleExcavatorChange = (e) => setSelectedExcavator(e.target.value);
  const handleTypeChange = (e) => setSelectedType(e.target.value);
  const applyAllFilters = () => fetchData(); // fetchData is already memoized with these dependencies
  const resetAllFilters = () => {
    setDateRange({ startDate: moment().subtract(6, "months").format("YYYY-MM-DD"), endDate: moment().format("YYYY-MM-DD") });
    setSelectedExcavator("all"); setSelectedType("all");
    // fetchData(); // Let useEffect handle it
  };

  const exportCSV = (data, filenamePrefix) => {
    if (!data || data.length === 0) { toast.warn(t("noDataToExport")); return; }
    const header = Object.keys(data[0]).join(",");
    const rows = data.map(row => Object.values(row).map(val => `"${String(val).replace(/"/g, '""')}"`).join(",")).join("\n");
    const csv = `${header}\n${rows}`;
    saveAs(new Blob([csv], { type: "text/csv;charset=utf-8;" }), `${filenamePrefix}_${moment().format("YYYYMMDD")}.csv`);
    toast.success(t("csvExportedSuccessfully"));
  };


  const chartOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { position: 'bottom', labels: { color: theme.palette.text.secondary } }, title: { display: true, color: theme.palette.text.primary } },
    scales: {
      x: { ticks: { color: theme.palette.text.secondary }, grid: { color: alpha(theme.palette.divider, 0.5) } },
      y: { beginAtZero: true, ticks: { color: theme.palette.text.secondary }, grid: { color: theme.palette.divider } }
    }
  };


  if (loading && !statsData.totalReports && !inventoryStats.total && !betriebstagebuchStats.total) { // Show loader only on initial full load
    return <PageContainer title={t("statistics")}><Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box></PageContainer>;
  }

  return (
    <PageContainer title={t("statisticsAndReports")}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }}>
        <Typography variant="h4" component="h1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><BarChartIcon color="primary" fontSize="large" />{t("applicationStatistics")}</Typography>
        <Button variant="outlined" onClick={() => window.print()} startIcon={<PrintIcon />}>{t("printPage")}</Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setError(null)}><AlertTitle>{t("error")}</AlertTitle>{error}</Alert>}

      <Paper variant="outlined" sx={{ p: 2, mb: 2.5, borderRadius: theme.shape.borderRadius }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={2.5}><TextField fullWidth label={t("startDate")} name="startDate" type="date" value={dateRange.startDate} onChange={handleDateChange} InputLabelProps={{ shrink: true }} size="small" /></Grid>
          <Grid item xs={12} sm={6} md={2.5}><TextField fullWidth label={t("endDate")} name="endDate" type="date" value={dateRange.endDate} onChange={handleDateChange} InputLabelProps={{ shrink: true }} size="small" inputProps={{ min: dateRange.startDate }} /></Grid>
          <Grid item xs={12} sm={6} md={2.5}><FormControl fullWidth size="small"><InputLabel>{t("excavator")}</InputLabel><Select value={selectedExcavator} label={t("excavator")} onChange={handleExcavatorChange}><MenuItem value="all">{t("allExcavators")}</MenuItem>{excavatorList.map(ex => <MenuItem key={ex._id} value={ex._id}>{ex.manufacturer} {ex.model}</MenuItem>)}</Select></FormControl></Grid>
          <Grid item xs={12} sm={6} md={2.5}><FormControl fullWidth size="small"><InputLabel>{t("reportType")}</InputLabel><Select value={selectedType} label={t("reportType")} onChange={handleTypeChange}><MenuItem value="all">{t("allTypes")}</MenuItem><MenuItem value="routine_maintenance">{t("routineMaintenance")}</MenuItem><MenuItem value="repair">{t("repair")}</MenuItem><MenuItem value="inspection">{t("inspection")}</MenuItem></Select></FormControl></Grid>
          <Grid item xs={12} md={2} container spacing={1} justifyContent="flex-end">
            <Grid item><Button variant="text" onClick={resetAllFilters} size="medium" startIcon={<RefreshIcon />}>{t("reset")}</Button></Grid>
            {/* <Grid item><Button variant="contained" onClick={applyAllFilters} size="medium" startIcon={<FilterIcon/>}>{t("apply")}</Button></Grid> */}
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 0 }}>
        <Tabs value={activeTab} onChange={(e, newVal) => setActiveTab(newVal)} indicatorColor="primary" textColor="primary" variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
          <Tab label={t("overview")} icon={<AssessmentIcon />} iconPosition="start" value="overview" />
          <Tab label={t("machines")} icon={<MachinesIcon />} iconPosition="start" value="machines" />
          <Tab label={t("personnel")} icon={<PersonnelIcon />} iconPosition="start" value="personnel" />
          <Tab label={t("predictions")} icon={<PredictionsIcon />} iconPosition="start" value="predictions" />
          <Tab label={t("inventory")} icon={<InventoryIcon />} iconPosition="start" value="inventory" />
          <Tab label={t("betriebstagebuch")} icon={<BetriebstagebuchIcon />} iconPosition="start" value="betriebstagebuch" />
        </Tabs>
      </Box>
      <Paper variant="outlined" sx={{ borderTopLeftRadius: 0, borderTopRightRadius: 0, borderColor: theme.palette.divider, mb: 3 }}>
        <TabPanel value={activeTab} index="overview">
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6} md={3}><DetailItemCard icon={<DescriptionIcon />} label={t("totalReports")} value={statsData.totalReports} accent /></Grid>
            <Grid item xs={12} sm={6} md={3}><DetailItemCard icon={<CheckCircleIcon />} label={t("signedReports")} value={statsData.signedReports} chipProps={{ label: `${((statsData.signedReports / statsData.totalReports) * 100 || 0).toFixed(0)}%`, color: 'success', size: 'small' }} /></Grid>
            <Grid item xs={12} sm={6} md={3}><DetailItemCard icon={<WarningIcon />} label={t("unsignedReports")} value={statsData.unsignedReports} chipProps={{ label: `${((statsData.unsignedReports / statsData.totalReports) * 100 || 0).toFixed(0)}%`, color: 'warning', size: 'small' }} /></Grid>
            <Grid item xs={12} sm={6} md={3}><DetailItemCard icon={<ListAltIcon />} label={t("betriebstagebuchTotal")} value={betriebstagebuchStats.total} /></Grid>

            <Grid item xs={12} lg={8}><Card variant="outlined"><CardHeader title={t("monthlyReports")} titleTypographyProps={{ variant: 'h6' }} /><CardContent sx={{ height: 300 }}><Bar data={{ labels: moment.monthsShort(), datasets: [{ label: t("reports"), data: statsData.monthlyReports, backgroundColor: chartBgColors[0], borderColor: chartBorderColors[0], borderWidth: 1 }] }} options={chartOptions} /></CardContent></Card></Grid>
            <Grid item xs={12} lg={4}><Card variant="outlined"><CardHeader title={t("reportStatusDistribution")} titleTypographyProps={{ variant: 'h6' }} /><CardContent sx={{ height: 300 }}><Doughnut data={{ labels: [t("signed"), t("unsigned")], datasets: [{ data: [statsData.signedReports, statsData.unsignedReports], backgroundColor: [chartBgColors[2], chartBgColors[3]], borderColor: [chartBorderColors[2], chartBorderColors[3]], borderWidth: 1 }] }} options={{ ...chartOptions, plugins: { ...chartOptions.plugins, legend: { position: 'right' } } }} /></CardContent></Card></Grid>
          </Grid>
        </TabPanel>
        <TabPanel value={activeTab} index="machines"> {/* Placeholder for Machines specific stats */}
          <Typography>{t("machineSpecificStatsComingSoon")}</Typography>
        </TabPanel>
        <TabPanel value={activeTab} index="personnel"> {/* Placeholder for Personnel specific stats */}
          <Typography>{t("personnelSpecificStatsComingSoon")}</Typography>
        </TabPanel>
        <TabPanel value={activeTab} index="predictions"> {/* Placeholder for Predictions */}
          <Typography>{t("predictiveAnalyticsComingSoon")}</Typography>
        </TabPanel>
        <TabPanel value={activeTab} index="inventory">
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">{t("inventoryOverview")}</Typography>
            <Button variant="outlined" startIcon={<DownloadCsvIcon />} onClick={() => exportCSV(inventoryStats.items, 'inventory_stats')}>{t("exportCSV")}</Button>
          </Stack>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6} md={3}><DetailItemCard icon={<InventoryIcon />} label={t("totalInventoryItems")} value={inventoryStats.total} accent /></Grid>
            <Grid item xs={12} sm={6} md={3}><DetailItemCard icon={<ListAltIcon />} label={t("itemCategories")} value={inventoryStats.categories.length} /></Grid>
            <Grid item xs={12} sm={6} md={6}><DetailItemCard icon={<CalendarIcon />} label={t("lastInventoryUpdate")} value={inventoryStats.lastUpdated ? moment(inventoryStats.lastUpdated).format("LLL") : t('notAvailable')} /></Grid>
            <Grid item xs={12}><TableContainer component={Paper} variant="outlined"><Table size="small"><TableHead><TableRow><TableCell>{t("maker")}</TableCell><TableCell>{t("model")}</TableCell><TableCell>{t("type")}</TableCell><TableCell align="right">{t("quantity")}</TableCell><TableCell>{t("lastUpdated")}</TableCell></TableRow></TableHead>
              <TableBody>{inventoryStats.items.slice(0, 10).map(item => (<TableRow hover key={item._id}><TableCell>{item.maker}</TableCell><TableCell>{item.model}</TableCell><TableCell>{item.type}</TableCell><TableCell align="right">{item.quantity}</TableCell><TableCell>{moment(item.updatedAt).format("L")}</TableCell></TableRow>))}</TableBody>
            </Table></TableContainer></Grid>
          </Grid>
        </TabPanel>
        <TabPanel value={activeTab} index="betriebstagebuch">
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">{t("betriebstagebuchOverview")}</Typography>
            <Button variant="outlined" startIcon={<DownloadCsvIcon />} onClick={() => exportCSV(betriebstagebuchStats.entries, 'betriebstagebuch_stats')}>{t("exportCSV")}</Button>
          </Stack>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6} md={2.4}><DetailItemCard icon={<ListAltIcon />} label={t("totalEntries")} value={betriebstagebuchStats.total} accent /></Grid>
            <Grid item xs={12} sm={6} md={2.4}><DetailItemCard icon={<CheckCircleIcon />} label={t("approvedEntries")} value={betriebstagebuchStats.approved} /></Grid>
            <Grid item xs={12} sm={6} md={2.4}><DetailItemCard icon={<PendingIcon />} label={t("unsignedEntries")} value={betriebstagebuchStats.unsigned} /></Grid>
            <Grid item xs={12} sm={6} md={2.4}><DetailItemCard icon={<CalendarIcon />} label={t("entriesThisMonth")} value={betriebstagebuchStats.thisMonth} /></Grid>
            <Grid item xs={12} sm={6} md={2.4}><DetailItemCard icon={<CalendarIcon />} label={t("lastEntryDate")} value={betriebstagebuchStats.lastEntryDate || t('notAvailable')} /></Grid>

            <Grid item xs={12} md={4}><Card variant="outlined"><CardHeader title={t("statusDistribution")} titleTypographyProps={{ variant: 'h6' }} /><CardContent sx={{ height: 250 }}><Pie data={{ labels: Object.keys(betriebstagebuchStats.statusDistribution), datasets: [{ data: Object.values(betriebstagebuchStats.statusDistribution), backgroundColor: chartBgColors, borderColor: chartBorderColors, borderWidth: 1 }] }} options={{ ...chartOptions, plugins: { ...chartOptions.plugins, legend: { position: 'right' } } }} /></CardContent></Card></Grid>
            <Grid item xs={12} md={4}><Card variant="outlined"><CardHeader title={t("monthlyActivity")} titleTypographyProps={{ variant: 'h6' }} /><CardContent sx={{ height: 250 }}><Bar data={{ labels: betriebstagebuchStats.monthlyCounts?.labels || [], datasets: [{ label: t('entries'), data: betriebstagebuchStats.monthlyCounts?.data || [], backgroundColor: chartBgColors[1], borderColor: chartBorderColors[1], borderWidth: 1 }] }} options={chartOptions} /></CardContent></Card></Grid>
            <Grid item xs={12} md={4}><Card variant="outlined"><CardHeader title={t("topCreators")} titleTypographyProps={{ variant: 'h6' }} /><CardContent sx={{ height: 250 }}><Bar data={{ labels: Object.keys(betriebstagebuchStats.topCreators), datasets: [{ label: t('entries'), data: Object.values(betriebstagebuchStats.topCreators), backgroundColor: chartBgColors[2], borderColor: chartBorderColors[2], borderWidth: 1 }] }} options={{ ...chartOptions, indexAxis: 'y' }} /></CardContent></Card></Grid>
          </Grid>
        </TabPanel>
      </Paper>
    </PageContainer>
  );
};

export default ReportStats;
